import type { B2BAssets, IntroBandSection } from '../types';
import { B2BCTAButton } from '../components/B2BCTAButton';

export interface B2BIntroBandProps {
    section: IntroBandSection;
    assets: B2BAssets;
}

/**
 * Editorial intro band — substantive eyebrow + long 80px body paragraph used
 * between sections in the brands / profs pages. Five instances in Figma:
 *
 * | Node          | Eyebrow                     | Figma h | Pill | Arrow |
 * | ------------- | --------------------------- | ------- | ---- | ----- |
 * | `443:24104`   | (About)                     | 572     |  Y   |   Y   |
 * | `480:29802`   | (Invite-only platform)      | 316     |  -   |   Y   |
 * | `480:29817`   | (Meet our Audience)         | 351     |  -   |   -   |
 * | `512:24642`   | (ArchiPro Media Program)    | 416     |  -   |   Y   |
 * | `443:24586`   | (Success Stories)           | 330     |  -   |   Y   |
 *
 * Built on the 1920-frame coordinate system used by the rest of the B2B
 * sections — `aspect-[1920/H]` with absolute-positioned children expressed in
 * vw / % so the band scales proportionally at any viewport. Mobile (≤md)
 * relaxes to a flowed stack.
 *
 * Body uses a first-line `text-indent` so the eyebrow (which is absolutely
 * positioned in the same column) sits inside the gap before the first word,
 * matching the Figma layout exactly.
 *
 * The About band also carries an "Apply to Join" pill below the body. In the
 * Figma source the body + pill live in a `flex flex-col gap-[123px]` column
 * anchored at (497.59, -14.33). The pill is then offset top:-12.17 from its
 * own 36-tall placeholder, so the effective visual gap from body bottom to
 * pill top is `123 - 12.17 = 110.83 px` (≈ 5.77vw at 1920 width). We replicate
 * that gap directly so the pill follows the body naturally as it reflows.
 *
 * 4 of the 5 bands also render a black right-pointing arrow vector in the
 * top-left corner at x=25.
 */
export const B2BIntroBand = ({ section }: B2BIntroBandProps) => {
    const {
        eyebrow,
        bodyLead,
        bodyTail,
        frameHeight,
        // `mobileFrameHeight` was used by the old absolute-positioned mobile
        // layout; the stacked flow doesn't need it but the prop stays on
        // the type so callers (and any new mobile variants) aren't broken.
        mobileCtaLabel,
        eyebrowPlacement = 'inline',
        bodyIndentPx = 295,
        bodyLeftPx = 498,
        bodyWidthPx = 1361,
        bodyTopPx = -14,
        eyebrowTrackingEm = -0.044,
        tailColor = '#c4c4c4',
        ctaLabel,
        ctaOpensModal = true,
        ctaHref = '#apply',
        cornerArrow = false,
        cornerArrowHeightPx = 46.6,
        cornerArrowTopPx = 7.7,
    } = section;

    // 1920-frame → vw / %
    const pctX = (px: number) => (px / 1920) * 100;
    const pctY = (px: number) => (px / frameHeight) * 100;

    const bodyLeftPct = pctX(bodyLeftPx);
    const bodyWidthPct = pctX(bodyWidthPx);
    const bodyTopPct = pctY(bodyTopPx);

    const eyebrowLeftPx = eyebrowPlacement === 'left-edge' ? 25 : 497.59;
    const eyebrowLeftPct = pctX(eyebrowLeftPx);
    const eyebrowTopPx = eyebrowPlacement === 'left-edge' ? 15.21 : 12.43;
    const eyebrowTopPct = pctY(eyebrowTopPx);

    const indentVw = bodyIndentPx / 19.2;

    // Corner arrow vector — top-left, 57.84 × cornerArrowHeightPx
    const arrowLeftPct = pctX(25);
    const arrowTopPct = pctY(cornerArrowTopPx);
    const arrowWidthPct = pctX(57.84);
    const arrowHeightPct = pctY(cornerArrowHeightPx);

    // Mobile (Figma `521:26868` for About, `521:26867` for Invite-only,
    // `521:27464` for Audience intro) is a stacked flow layout — only the
    // CTA pill keeps its Figma-spec dimensions (257.87 × 67.19 in the
    // 768 frame). Eyebrow + body sizes are inlined below in the mobile
    // markup so the absolute frame-positioning vars are no longer needed.
    const mobileCtaWidth = (257.87 / 768) * 100;
    const mobileCtaHeight = (67.19 / 768) * 100;

    return (
        <section
            // Desktop keeps the fixed Figma aspect ratio; mobile drops it
            // (`aspect-auto!`) so the stacked flow layout below sizes to
            // content. Long eyebrows no longer mash into the body text.
            className="relative w-full bg-white max-md:aspect-auto! max-md:bg-transparent"
            style={{ aspectRatio: `1920 / ${frameHeight}` }}
        >
            {/* Desktop layout — absolute-positioned to match 1920 frame */}
            <div className="absolute inset-0 max-md:hidden">
                {/* Corner arrow vector — same SVG used in B2BFeature */}
                {cornerArrow && (
                    <svg
                        className="absolute"
                        style={{
                            left: `${arrowLeftPct}%`,
                            top: `${arrowTopPct}%`,
                            width: `${arrowWidthPct}%`,
                            height: `${arrowHeightPct}%`,
                        }}
                        viewBox="0 0 58 62"
                        preserveAspectRatio="none"
                        fill="none"
                        aria-hidden="true"
                    >
                        <path
                            d="M2 31 L54 31 M44 21 L54 31 L44 41"
                            stroke="#181818"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        />
                    </svg>
                )}

                {/* Eyebrow sits absolutely in the same column-x as the body so
                    its visual position falls inside the body's first-line
                    text-indent gap (Figma overlap pattern). */}
                <p
                    className="absolute whitespace-nowrap font-sans text-[1.46vw] leading-[1.07] text-[#181818]"
                    style={{
                        left: `${eyebrowLeftPct}%`,
                        top: `${eyebrowTopPct}%`,
                        letterSpacing: `${eyebrowTrackingEm}em`,
                    }}
                >
                    {eyebrow}
                </p>

                {/* Body + CTA share a Figma-faithful flex column with a
                    5.77vw (=110.83 px) visual gap between body bottom and
                    pill top, so the pill auto-follows the body. */}
                <div
                    className="absolute flex flex-col items-start gap-[5.77vw]"
                    style={{
                        left: `${bodyLeftPct}%`,
                        top: `${bodyTopPct}%`,
                        width: `${bodyWidthPct}%`,
                    }}
                >
                    <p
                        className="font-sans text-[4.17vw] leading-[1.07] tracking-[-0.048em] text-[#181818] [word-break:break-word]"
                        style={{ textIndent: `${indentVw}vw` }}
                    >
                        <span>{bodyLead}</span>
                        {bodyTail && (
                            <span style={{ color: tailColor }}>{bodyTail}</span>
                        )}
                    </p>

                    {/* CTA pill — About band only (Figma `505:24961`).
                        230×60 sized in vw; label 22.7px Neue Haas Bold with
                        leading 24.324 / 22.703 = 1.071. */}
                    {ctaLabel &&
                        (ctaOpensModal ? (
                            <B2BCTAButton
                                label={ctaLabel}
                                color="dark"
                                hideArrow
                                className="inline-flex h-[3.125vw] w-[11.98vw] items-center justify-center whitespace-nowrap rounded-full bg-[#333] px-[1.436vw] py-[0.591vw] text-[1.183vw] font-bold leading-[1.071] tracking-[-0.030em] text-white shadow-[0_3.243px_11.351px_rgba(0,0,0,0.11)]"
                            />
                        ) : (
                            <a
                                href={ctaHref}
                                className="inline-flex h-[3.125vw] w-[11.98vw] items-center justify-center whitespace-nowrap rounded-full bg-[#333] px-[1.436vw] py-[0.591vw] font-sans text-[1.183vw] font-bold leading-[1.071] tracking-[-0.030em] text-white shadow-[0_3.243px_11.351px_rgba(0,0,0,0.11)]"
                            >
                                {ctaLabel}
                            </a>
                        ))}
                </div>
            </div>

            {/* Mobile layout — stacked flow. The Figma spec uses an inline
             * eyebrow-in-text-indent pattern (eyebrow tucked into the body's
             * first-line indent), but at 414 viewport long eyebrows like
             * "(ArchiPro Media Programme)" overflow the indent and mash into
             * the body. Stacking eyebrow above body reads cleanly across all
             * eyebrow lengths.
             *
             * Aspect-ratio dropped on mobile too — the section is now
             * content-sized so longer bodies don't overflow the fixed frame. */}
            <div className="hidden flex-col gap-[3vw] px-[5.41%] py-[2vw] max-md:flex">
                {/* Eyebrow size 3.255vw matches the canonical mobile eyebrow
                 *  token used by Pillar / Profile Showcase / Audience so the
                 *  parenthetical labels read at one consistent size across
                 *  every section of the page. */}
                <p className="font-sans text-[3.255vw] leading-[1.07] tracking-[-0.044em] text-[#181818]">
                    {eyebrow}
                </p>
                <p
                    // Body leading 1.2 on mobile for readability (desktop
                    // Figma spec is 1.07 but reads cramped at 414).
                    className="font-sans leading-[1.2] tracking-[-0.048em] text-[#181818] [word-break:break-word]"
                    style={{ fontSize: `${(40 / 768) * 100}vw` }}
                >
                    <span>{bodyLead}</span>
                    {bodyTail && (
                        <span style={{ color: tailColor }}>{bodyTail}</span>
                    )}
                </p>
                {(mobileCtaLabel ?? ctaLabel) && (
                    <div
                        className="mt-[3vw]"
                        style={{
                            width: `${mobileCtaWidth}vw`,
                            height: `${mobileCtaHeight}vw`,
                        }}
                    >
                        <B2BCTAButton
                            label={mobileCtaLabel ?? ctaLabel!}
                            color="dark"
                            hideArrow
                            className="inline-flex size-full items-center justify-center whitespace-nowrap rounded-full bg-[#333] font-bold leading-[1.071] tracking-[-0.030em] text-white shadow-[0_3.243px_11.351px_rgba(0,0,0,0.11)]"
                            labelStyle={{
                                fontSize: `${(25.424 / 768) * 100}vw`,
                            }}
                        />
                    </div>
                )}
            </div>
        </section>
    );
};
